import React, { useState, useRef, useEffect } from "react";
import { Mic, MicOff } from "lucide-react";
import { useTranslation } from "react-i18next";

const SILENCE_THRESHOLD = 1.2; // Umbral RMS ajustado (similar a DictateButton)
const SILENCE_DURATION = 2000; // 2 segundos

export const VoiceButton = ({
  onTranscript,
  isLoading,
  disabled,
  onRecordingStateChange,
  onAnalyserChange,
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const silenceStartRef = useRef(null);
  const silenceCheckRef = useRef(null);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      if (silenceCheckRef.current) {
        clearInterval(silenceCheckRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(isRecording || isProcessing);
    }
  }, [isRecording, isProcessing, onRecordingStateChange]);

  useEffect(() => {
    if (onAnalyserChange) {
      onAnalyserChange(analyserRef.current);
    }
  }, [analyserRef.current, onAnalyserChange]);

  const detectSilence = () => {
    if (!analyserRef.current || !dataArrayRef.current) {
      return;
    }

    analyserRef.current.getByteTimeDomainData(dataArrayRef.current);

    let sum = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const sample = (dataArrayRef.current[i] - 128) / 128;
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / dataArrayRef.current.length);
    const volume = rms * 100;

    if (volume < SILENCE_THRESHOLD) {
      if (!silenceStartRef.current) {
        silenceStartRef.current = Date.now();
      }

      const silenceDuration = Date.now() - silenceStartRef.current;

      if (silenceDuration > SILENCE_DURATION) {
        stopRecording();
        return;
      }
    } else {
      silenceStartRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert(t("voice.unsupported"));
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      analyserRef.current.fftSize = 1024;
      analyserRef.current.smoothingTimeConstant = 0.4;
      analyserRef.current.minDecibels = -85;
      analyserRef.current.maxDecibels = -15;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      silenceStartRef.current = null;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (silenceCheckRef.current) {
          clearInterval(silenceCheckRef.current);
          silenceCheckRef.current = null;
        }

        if (
          audioContextRef.current &&
          audioContextRef.current.state !== "closed"
        ) {
          audioContextRef.current.close();
        }

        silenceStartRef.current = null;

        if (audioChunksRef.current.length === 0) {
          console.warn("No audio data recorded");
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        setIsProcessing(true);

        try {
          await onTranscript(audioBlob);
        } catch (error) {
          console.error("Transcription error:", error);
          alert(t("voice.error"));
        } finally {
          setIsProcessing(false);
          audioChunksRef.current = [];
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);

      silenceCheckRef.current = setInterval(() => {
        detectSilence();
      }, 100);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        alert(t("voice.permissionDenied"));
      } else {
        alert(t("voice.unsupported"));
      }
    }
  };

  const stopRecording = () => {
    if (!isRecording && mediaRecorderRef.current?.state !== "recording") {
      return;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }

    if (silenceCheckRef.current) {
      clearInterval(silenceCheckRef.current);
      silenceCheckRef.current = null;
    }

    silenceStartRef.current = null;
    setIsRecording(false);

    if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const isDisabled = disabled || isLoading || isProcessing;
  const buttonTitle = isProcessing
    ? t("voice.processing")
    : isRecording
    ? t("voice.stop")
    : t("voice.record");

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={`gpc-voice-button ${isRecording ? "gpc-voice-recording" : ""}`}
      aria-label={buttonTitle}
      title={buttonTitle}
    >
      {isProcessing ? (
        <span className="gpc-spinner" />
      ) : isRecording ? (
        <MicOff className="gpc-icon gpc-voice-icon-active" />
      ) : (
        <Mic className="gpc-icon" />
      )}
    </button>
  );
};